
import React, { useState } from 'react';
import { MicVocal, X, AlertTriangle, Check, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import {
  signInWithGoogle,
  signUpWithEmail,
  signInWithEmail,
  sendMagicLink,
} from '../services/supabase';

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path>
    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path>
    <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path>
    <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571l6.19,5.238C42.022,35.37,44,30.038,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path>
  </svg>
);

interface LoginModalProps {
  onLoginSuccess: () => void;
  onClose: () => void;
}

type Tab = 'google' | 'email' | 'magic';
type EmailMode = 'signin' | 'signup';

const LoginModal: React.FC<LoginModalProps> = ({ onLoginSuccess, onClose }) => {
  const [activeTab, setActiveTab] = useState<Tab>('google');
  const [emailMode, setEmailMode] = useState<EmailMode>('signin');

  // Form State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [magicEmail, setMagicEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // Status State
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);

  const handleAuthError = (e: any) => {
    console.error('Auth Error:', e);
    const msg: string = e?.message ?? '';
    if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('already been registered')) {
      setError('An account with this email already exists. Try signing in.');
    } else if (msg.toLowerCase().includes('invalid login credentials') || msg.toLowerCase().includes('invalid credentials')) {
      setError('Incorrect email or password.');
    } else if (msg.toLowerCase().includes('email not confirmed')) {
      setError('Please verify your email before signing in.');
    } else if (msg.toLowerCase().includes('password should be at least')) {
      setError('Password must be at least 6 characters.');
    } else if (msg) {
      setError(msg);
    } else {
      setError('Failed to sign in. Please try again.');
    }
  };

  const clearErrors = () => {
    setError('');
  };

  // --- Google ---

  const handleGoogleLogin = async () => {
    if (isLoading) return;
    clearErrors();
    setIsLoading(true);
    try {
      await signInWithGoogle(); // Triggers OAuth redirect — page navigates away on success
    } catch (e: any) {
      handleAuthError(e);
      setIsLoading(false);
    }
  };

  // --- Email / Password ---

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    if (!email.trim() || !password.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    setIsLoading(true);
    try {
      if (emailMode === 'signup') {
        await signUpWithEmail(email.trim(), password);
        setVerificationSent(true);
      } else {
        await signInWithEmail(email.trim(), password);
        onLoginSuccess();
      }
    } catch (e: any) {
      handleAuthError(e);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Magic Link ---

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    if (!magicEmail.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setIsLoading(true);
    try {
      await sendMagicLink(magicEmail.trim());
      setMagicLinkSent(true);
    } catch (e: any) {
      handleAuthError(e);
    } finally {
      setIsLoading(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'google', label: 'Google' },
    { id: 'email', label: 'Email' },
    { id: 'magic', label: 'Magic Link' },
  ];

  const renderGoogleTab = () => (
    <div className="space-y-4">
      <button
        type="button"
        onClick={handleGoogleLogin}
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-white text-gray-800 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed border border-gray-200"
      >
        <GoogleIcon />
        Sign in with Google
      </button>
    </div>
  );

  const renderEmailTab = () => {
    if (verificationSent) {
      return (
        <div className="text-center space-y-3 py-2">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full">
            <Check size={24} className="text-green-600 dark:text-green-400" />
          </div>
          <p className="font-semibold text-base-content dark:text-dark-content">Check your inbox</p>
          <p className="text-sm text-base-content/70 dark:text-dark-content/70">
            We sent a verification email to <strong>{email}</strong>. Click the link to activate your account, then sign in.
          </p>
          <button
            onClick={() => { setVerificationSent(false); setEmailMode('signin'); }}
            className="text-sm text-brand-primary hover:underline"
          >
            Back to sign in
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex rounded-lg overflow-hidden border border-base-300 dark:border-dark-300 text-sm font-medium">
          {(['signin', 'signup'] as EmailMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => { setEmailMode(mode); clearErrors(); }}
              className={`flex-1 py-2 transition-colors ${emailMode === mode ? 'bg-brand-primary text-white' : 'bg-base-200 dark:bg-dark-100 text-base-content/70 dark:text-dark-content/70 hover:bg-base-300 dark:hover:bg-dark-300'}`}
            >
              {mode === 'signin' ? 'Sign In' : 'Sign Up'}
            </button>
          ))}
        </div>

        <form onSubmit={handleEmailSubmit} className="space-y-3">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50 dark:text-dark-content/50" size={18} />
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (error) clearErrors(); }}
              className="w-full pl-10 pr-4 py-3 bg-base-200 dark:bg-dark-100 border border-base-300 dark:border-dark-300 rounded-lg placeholder-base-content/50 dark:placeholder-dark-content/50 focus:ring-2 focus:ring-brand-primary focus:outline-none transition-all"
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50 dark:text-dark-content/50" size={18} />
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (error) clearErrors(); }}
              className="w-full pl-10 pr-10 py-3 bg-base-200 dark:bg-dark-100 border border-base-300 dark:border-dark-300 rounded-lg placeholder-base-content/50 dark:placeholder-dark-content/50 focus:ring-2 focus:ring-brand-primary focus:outline-none transition-all"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/50 dark:text-dark-content/50 hover:text-base-content dark:hover:text-dark-content transition-colors"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <button
            type="submit"
            disabled={isLoading || !email.trim() || !password.trim()}
            className="w-full py-3 px-4 bg-brand-primary text-white font-bold rounded-lg hover:bg-brand-dark transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            ) : (
              emailMode === 'signup' ? 'Create Account' : 'Sign In'
            )}
          </button>
        </form>
      </div>
    );
  };

  const renderMagicTab = () => {
    if (magicLinkSent) {
      return (
        <div className="text-center space-y-3 py-2">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-brand-primary/10 rounded-full">
            <Mail size={24} className="text-brand-primary" />
          </div>
          <p className="font-semibold text-base-content dark:text-dark-content">Magic link sent!</p>
          <p className="text-sm text-base-content/70 dark:text-dark-content/70">
            Check <strong>{magicEmail}</strong> for your sign-in link. Click it to log in instantly — no password needed.
          </p>
          <button
            onClick={() => { setMagicLinkSent(false); setMagicEmail(''); }}
            className="text-sm text-brand-primary hover:underline"
          >
            Send to a different email
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <p className="text-sm text-base-content/70 dark:text-dark-content/70">
          Enter your email and we'll send you a magic sign-in link — no password required.
        </p>
        <form onSubmit={handleMagicLinkSubmit} className="space-y-3">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50 dark:text-dark-content/50" size={18} />
            <input
              type="email"
              placeholder="you@example.com"
              value={magicEmail}
              onChange={(e) => { setMagicEmail(e.target.value); if (error) clearErrors(); }}
              className="w-full pl-10 pr-4 py-3 bg-base-200 dark:bg-dark-100 border border-base-300 dark:border-dark-300 rounded-lg placeholder-base-content/50 dark:placeholder-dark-content/50 focus:ring-2 focus:ring-brand-primary focus:outline-none transition-all"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || !magicEmail.trim()}
            className="w-full py-3 px-4 bg-brand-primary text-white font-bold rounded-lg hover:bg-brand-dark transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            ) : (
              'Send Magic Link'
            )}
          </button>
        </form>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-heading"
    >
      <div
        className="relative w-full max-w-sm mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-base-100 dark:bg-dark-200 p-8 rounded-2xl shadow-2xl border border-base-300 dark:border-dark-300 text-base-content dark:text-dark-content">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 rounded-full text-base-content/70 dark:text-dark-content/70 hover:bg-base-200 dark:hover:bg-dark-300 transition-colors"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>

          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-full mb-3 shadow-lg">
              <MicVocal className="text-white" size={32} />
            </div>
            <h1 id="login-heading" className="text-2xl font-bold">Sign In</h1>
            <p className="text-base-content/70 dark:text-dark-content/70 mt-1">Choose how you'd like to continue.</p>
          </div>

          {/* Tab Bar */}
          <div className="flex rounded-xl overflow-hidden border border-base-300 dark:border-dark-300 mb-5 text-xs font-semibold">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => { setActiveTab(tab.id); clearErrors(); }}
                className={`flex-1 py-2 transition-colors ${activeTab === tab.id ? 'bg-brand-primary text-white' : 'bg-base-200 dark:bg-dark-100 text-base-content/70 dark:text-dark-content/70 hover:bg-base-300 dark:hover:bg-dark-300'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Error banner */}
          {error && (
            <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs rounded-lg flex items-start gap-2 mb-4">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span className="break-words">{error}</span>
            </div>
          )}

          {/* Tab Content */}
          {activeTab === 'google' && renderGoogleTab()}
          {activeTab === 'email' && renderEmailTab()}
          {activeTab === 'magic' && renderMagicTab()}
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
